import { useState, useEffect } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { toast } from "sonner";

import { API } from "../../config/api";

export default function AdminSponsors() {
  const [tiers, setTiers] = useState([]);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: "",
    price_range_text: "",
    benefits: "",
    order_index: 0
  });
  const [logoForm, setLogoForm] = useState({
    name: "",
    url: "",
    website: "",
    tier_id: "",
    order_index: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchData = async () => {
    try {
      const [tiersRes, logosRes] = await Promise.all([
        fetch(`${API}/admin/sponsor-tiers`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } }),
        fetch(`${API}/admin/sponsor-logos`, { headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } })
      ]);

      if (tiersRes.ok) setTiers(await tiersRes.json());
      if (logosRes.ok) setLogos(await logosRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Tier handlers
  const handleOpenTierDialog = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name,
        price_range_text: tier.price_range_text,
        benefits: tier.benefits.join("\n"),
        order_index: tier.order_index
      });
    } else {
      setEditingTier(null);
      setTierForm({ name: "", price_range_text: "", benefits: "", order_index: 0 });
    }
    setTierDialogOpen(true);
  };

  const handleTierSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...tierForm,
      benefits: tierForm.benefits.split("\n").filter(b => b.trim())
    };

    try {
      const url = editingTier 
        ? `${API}/admin/sponsor-tiers/${editingTier.id}`
        : `${API}/admin/sponsor-tiers`;
      
      const response = await fetch(url, {
        method: editingTier ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingTier ? "Tier updated!" : "Tier created!");
        setTierDialogOpen(false);
        fetchData();
      } else {
        toast.error("Failed to save tier");
      }
    } catch (error) {
      toast.error("Error saving tier");
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!confirm("Delete this tier?")) return;

    try {
      const response = await fetch(`${API}/admin/sponsor-tiers/${tierId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("Tier deleted!");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deleting tier");
    }
  };

  // Logo handlers
  const handleLogoSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API}/admin/sponsor-logos`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(logoForm)
      });

      if (response.ok) {
        toast.success("Sponsor logo added!");
        setLogoDialogOpen(false);
        setLogoForm({ name: "", url: "", website: "", tier_id: "", order_index: 0 });
        fetchData();
      } else {
        toast.error("Failed to add logo");
      }
    } catch (error) {
      toast.error("Error adding logo");
    }
  };

  const handleDeleteLogo = async (logoId) => {
    if (!confirm("Delete this sponsor logo?")) return;

    try {
      const response = await fetch(`${API}/admin/sponsor-logos/${logoId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });

      if (response.ok) {
        toast.success("Logo deleted!");
        fetchData();
      }
    } catch (error) {
      toast.error("Error deleting logo");
    }
  };

  const getTierName = (tierId) => tiers.find(t => t.id === tierId)?.name || "Unknown";

  return (
    <div className="space-y-6" data-testid="admin-sponsors">
      <h1 className="font-heading text-2xl text-kerala-green">Sponsors</h1>

      <Tabs defaultValue="tiers">
        <TabsList>
          <TabsTrigger value="tiers">Sponsor Tiers</TabsTrigger>
          <TabsTrigger value="logos">Sponsor Logos</TabsTrigger>
        </TabsList>

        {/* Tiers Tab */}
        <TabsContent value="tiers" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={tierDialogOpen} onOpenChange={setTierDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-kerala-green hover:bg-kerala-green/90"
                  onClick={() => handleOpenTierDialog()}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Tier
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingTier ? "Edit Tier" : "Add Tier"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTierSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="tier_name">Tier Name</Label>
                    <Input
                      id="tier_name"
                      value={tierForm.name}
                      onChange={(e) => setTierForm({ ...tierForm, name: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Gold, Silver, Bronze"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="price_range">Price Range</Label>
                    <Input
                      id="price_range"
                      value={tierForm.price_range_text}
                      onChange={(e) => setTierForm({ ...tierForm, price_range_text: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., $2,500+"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="benefits">Benefits (one per line)</Label>
                    <Textarea
                      id="benefits"
                      value={tierForm.benefits}
                      onChange={(e) => setTierForm({ ...tierForm, benefits: e.target.value })}
                      rows={4}
                      className="mt-1"
                      placeholder="Logo on materials&#10;Stage mention&#10;VIP seating"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="tier_order">Order</Label>
                    <Input
                      id="tier_order"
                      type="number"
                      value={tierForm.order_index}
                      onChange={(e) => setTierForm({ ...tierForm, order_index: parseInt(e.target.value) || 0 })}
                      className="mt-1"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTierDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-kerala-green hover:bg-kerala-green/90">
                      {editingTier ? "Update" : "Create"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="bg-white rounded-sm border border-kerala-gold/10">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto" />
              </div>
            ) : tiers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No tiers yet.</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Price Range</TableHead>
                    <TableHead>Benefits</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tiers.map(tier => (
                    <TableRow key={tier.id}>
                      <TableCell className="font-medium">{tier.name}</TableCell>
                      <TableCell>{tier.price_range_text}</TableCell>
                      <TableCell>
                        <ul className="text-sm text-gray-600 list-disc list-inside">
                          {tier.benefits.slice(0, 3).map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                          {tier.benefits.length > 3 && (
                            <li className="text-gray-400">+{tier.benefits.length - 3} more</li>
                          )}
                        </ul>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => handleOpenTierDialog(tier)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDeleteTier(tier.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Logos Tab */}
        <TabsContent value="logos" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={logoDialogOpen} onOpenChange={setLogoDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-kerala-green hover:bg-kerala-green/90" disabled={tiers.length === 0}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Sponsor Logo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Sponsor Logo</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleLogoSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="logo_name">Sponsor Name</Label>
                    <Input
                      id="logo_name"
                      value={logoForm.name}
                      onChange={(e) => setLogoForm({ ...logoForm, name: e.target.value })}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="logo_url">Logo URL</Label>
                    <Input
                      id="logo_url"
                      value={logoForm.url}
                      onChange={(e) => setLogoForm({ ...logoForm, url: e.target.value })}
                      className="mt-1"
                      placeholder="https://..."
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="logo_website">Website (optional)</Label>
                    <Input
                      id="logo_website"
                      value={logoForm.website}
                      onChange={(e) => setLogoForm({ ...logoForm, website: e.target.value })}
                      className="mt-1"
                      placeholder="https://..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="logo_tier">Tier</Label>
                    <Select
                      value={logoForm.tier_id}
                      onValueChange={(value) => setLogoForm({ ...logoForm, tier_id: value })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select tier" />
                      </SelectTrigger>
                      <SelectContent>
                        {tiers.map(tier => (
                          <SelectItem key={tier.id} value={tier.id}>{tier.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setLogoDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-kerala-green hover:bg-kerala-green/90">
                      Add
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {tiers.length === 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 text-yellow-800">
              Please create sponsor tiers first.
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {logos.map(logo => (
              <div key={logo.id} className="group relative bg-white border border-kerala-gold/10 rounded-sm p-4 text-center">
                <img 
                  src={logo.url} 
                  alt={logo.name}
                  className="h-16 w-auto mx-auto object-contain"
                />
                <p className="mt-2 text-sm font-medium truncate">{logo.name}</p>
                <p className="text-xs text-gray-500">{getTierName(logo.tier_id)}</p>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 text-red-600"
                  onClick={() => handleDeleteLogo(logo.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
